import React, { useCallback, useMemo } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { MultiScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { useGetRetentionPoliciesQuery, useCreateRetentionPolicyMutation, useUpdateRetentionPolicyMutation } from '../../../store/account/account'
import { RetentionResourceType, RetentionRuleType, AuditLogRetention, RetentionPolicy, TTLRetentionRule } from '../../../models/RetentionPolicy'
import '../Settings/Settings.scss'

const DEFAULT_SEARCHABLE_RETENTION_HOURS = 7 * 24
const DEFAULT_ARCHIVE_RETENTION_HOURS = 30 * 24

const SEARCHABLE_RULE: TTLRetentionRule = {
  type: RetentionRuleType.TTL,
  config: { value: DEFAULT_SEARCHABLE_RETENTION_HOURS, unit: 'hour' },
}
const ARCHIVED_RULE: TTLRetentionRule = {
  type: RetentionRuleType.TTL,
  config: { value: DEFAULT_ARCHIVE_RETENTION_HOURS, unit: 'hour' },
}

type AuditLogKind = 'searchable_log' | 'archived_log'

async function upsertAuditLogPolicy(
  accountId: string,
  kind: AuditLogKind,
  defaultEnabled: boolean,
  rule: TTLRetentionRule,
  existingPolicy: RetentionPolicy | undefined,
  isDefaultPolicy: (p: RetentionPolicy | undefined) => boolean,
  createPolicy: (arg: { accountId: string; body: { rule: TTLRetentionRule; resource: AuditLogRetention } }) => { unwrap: () => Promise<void> },
  updatePolicy: (arg: { accountId: string; policyId: string; body: { rule: TTLRetentionRule; resource: AuditLogRetention } }) => {
    unwrap: () => Promise<void>
  },
): Promise<void> {
  const resource: AuditLogRetention = { type: RetentionResourceType.AUDIT_LOG, config: { kind, defaultEnabled } }
  if (existingPolicy) {
    if (isDefaultPolicy(existingPolicy)) {
      await createPolicy({ accountId, body: { rule, resource } }).unwrap()
    } else {
      await updatePolicy({ accountId, policyId: existingPolicy.id, body: { rule, resource } }).unwrap()
    }
  } else {
    await createPolicy({ accountId, body: { rule, resource } }).unwrap()
  }
}

export const AuditLogSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { hasQuota } = useZocksQuota()
  const auditLogsEnabledSettingAvailable = hasQuota('auth.auditLogEnabled.value')

  const { data: retentionPoliciesData, isFetching: isRetentionPoliciesFetching } = useGetRetentionPoliciesQuery(
    { accountId },
    { refetchOnMountOrArgChange: true },
  )
  const [createRetentionPolicy] = useCreateRetentionPolicyMutation()
  const [updateRetentionPolicy] = useUpdateRetentionPolicyMutation()

  const isDefaultPolicy = (policy: RetentionPolicy | undefined): boolean =>
    policy?.isDefault === true || (policy as { is_default?: boolean })?.is_default === true
  const getAuditLogPolicyEnabled = (policy: RetentionPolicy | undefined): boolean => {
    const defaultEnabled = (policy?.resource as AuditLogRetention | undefined)?.config?.defaultEnabled
    return defaultEnabled !== false
  }

  const searchableLogPolicy = useMemo(() => {
    return retentionPoliciesData?.data?.find(
      (policy) => policy.resource.type === RetentionResourceType.AUDIT_LOG && (policy.resource as AuditLogRetention).config?.kind === 'searchable_log',
    )
  }, [retentionPoliciesData])

  const archivedLogPolicy = useMemo(() => {
    return retentionPoliciesData?.data?.find(
      (policy) => policy.resource.type === RetentionResourceType.AUDIT_LOG && (policy.resource as AuditLogRetention).config?.kind === 'archived_log',
    )
  }, [retentionPoliciesData])

  const searchableLogsEnabled = !!searchableLogPolicy && getAuditLogPolicyEnabled(searchableLogPolicy)
  const archiveRetentionEnabled = !!archivedLogPolicy && getAuditLogPolicyEnabled(archivedLogPolicy)

  const isToggleDisabled = isRetentionPoliciesFetching || !auditLogsEnabledSettingAvailable

  const handleToggleSearchableLogs = useCallback(
    async (checked: boolean) => {
      await upsertAuditLogPolicy(
        accountId,
        'searchable_log',
        checked,
        SEARCHABLE_RULE,
        searchableLogPolicy,
        isDefaultPolicy,
        createRetentionPolicy,
        updateRetentionPolicy,
      )
      if (!checked && archivedLogPolicy) {
        await upsertAuditLogPolicy(
          accountId,
          'archived_log',
          false,
          ARCHIVED_RULE,
          archivedLogPolicy,
          isDefaultPolicy,
          createRetentionPolicy,
          updateRetentionPolicy,
        )
      }
    },
    [accountId, searchableLogPolicy, archivedLogPolicy, createRetentionPolicy, updateRetentionPolicy],
  )

  const handleToggleArchiveRetention = useCallback(
    async (checked: boolean) => {
      await upsertAuditLogPolicy(
        accountId,
        'archived_log',
        checked,
        ARCHIVED_RULE,
        archivedLogPolicy,
        isDefaultPolicy,
        createRetentionPolicy,
        updateRetentionPolicy,
      )
    },
    [accountId, archivedLogPolicy, createRetentionPolicy, updateRetentionPolicy],
  )

  return (
    <Container className='d-flex flex-column align-items-start zocks-gap-1'>
      <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded w-100 m-0'>
        <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
          <div className='d-flex align-self-center gap-3 align-items-center'>
            <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
            <div className='d-flex flex-column'>
              <span className='zocks-text-gray-900 mb-1'>Audit logs track account activity for compliance and security:</span>
              <ul className='mb-0 ps-3'>
                <li className='zocks-text-gray-900'>Searchable logs: Instant access to query and search logs.</li>
                <li className='zocks-text-gray-900'>Archived logs: Extended retention after searchable period ends. Request it from Support.</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>
      <Row className='gap-3 p-0 align-items-center w-100 m-0 mb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>Enable searchable logs</div>
        </Col>
        <Col className='p-0'>
          <MultiScopedBlock
            scopedBlocks={[
              {
                scope: { scope: 'account:compliance_config_update', superScope: [] },
                missingScopeAction: MissingScopeAction.DISABLE,
              },
              {
                scope: { scope: 'account:update', superScope: ['account:admin'] },
                missingScopeAction: MissingScopeAction.DISABLE,
              },
            ]}
          >
            <Form.Switch disabled={isToggleDisabled} checked={searchableLogsEnabled} onChange={(e) => handleToggleSearchableLogs(e.currentTarget.checked)} />
          </MultiScopedBlock>
        </Col>
      </Row>
      <Row className='gap-3 p-0 align-items-center w-100 m-0 mb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>Enable archived logs</div>
        </Col>
        <Col className='p-0'>
          <MultiScopedBlock
            scopedBlocks={[
              {
                scope: { scope: 'account:compliance_config_update', superScope: [] },
                missingScopeAction: MissingScopeAction.DISABLE,
              },
              {
                scope: { scope: 'account:update', superScope: ['account:admin'] },
                missingScopeAction: MissingScopeAction.DISABLE,
              },
            ]}
          >
            <Form.Switch
              disabled={isToggleDisabled || !searchableLogsEnabled}
              checked={archiveRetentionEnabled}
              onChange={(e) => {
                handleToggleArchiveRetention(e.currentTarget.checked)
              }}
            />
          </MultiScopedBlock>
        </Col>
      </Row>
    </Container>
  )
}
